// EasyAuth (Azure App Service 認証) 再認証誘導の状態機械。
//
// 背景: 検証環境のみ EasyAuth が有効で、かつ本アプリは PWA (ssr:false) のためトップの
// HTML ナビゲーションがサーバーに届かず EasyAuth セッションが確立されない。この状態で
// CSR から /api/v2/Cookie2Token を呼ぶと EasyAuth が 302 (login.microsoftonline.com) を
// 返す。これを検出したら /.auth/login/aad へフルページ遷移して EasyAuth セッションを
// 張り直す。本番は EasyAuth 無しのため発生しない。
//
// この composable は誘導にまつわる状態を 1 箇所に集約する。関係する難所は 3 つ:
//  (a) 無限ループ防止: 再認証から戻ってもまた 302 なら、再々誘導せず通常の未ログインに委ねる。
//  (b) 並行呼び出し: Cookie2Token は複数コンポーネントから並行に呼ばれ得る (in-flight 共有で
//      同一 opaqueredirect を複数 waiter が受け取る)。誘導 / ループ判定は 1 回だけ効かせる。
//  (c) SPA 遷移競合: window.location.href のフルページ遷移は非同期で、その間に
//      navigateTo("LOGIN") 等の SPA 遷移が走ると競合する。遷移開始後は後続ルーティングを止める。
//
// 状態遷移 (1 回の Cookie2Token 試行):
//   beginAttempt: 戻りガードを消費し justReturned を確定 (再認証から戻った直後の試行か)
//   ├ 302 検出 → handleEasyAuthRedirect:
//   │    justReturned=true (復帰直後にまた 302) → loopDetected を立て誘導しない (通常フローへ)
//   │    それ以外 (loopDetected/navigatingAway でない) → /.auth/login/aad へフルページ遷移
//   └ 302 以外で決着 (成功/非 302 失敗) → endAttempt: justReturned をリセット
//
// justReturned は「復帰直後の 1 回限り」を表し試行完了でリセットする。一方 loopDetected は
// 真のループを検出した永続フラグ。両者を分けることで、復帰後に一度通常利用してから
// 後で再びセッション切れ (302) になっても、justReturned が残っておらず正しく再誘導できる。
//
// ループ判定 (justReturned の元) はページリロードをまたぐため sessionStorage を用いる。
// 並行・遷移状態および justReturned/loopDetected は同一ページ寿命の同期フラグ (module scope)。

//   状態遷移の整理
// 
//   ┌──────┐                ┌──────────────┐  ┌──────────────┐  ┌────────────────┐   ┌─────────────────┐
//   │ Page │                │ beginAttempt │  │ Cookie2Token │  │ handleRedirect │   │ checkPermission │
//   └───┬──┘                └───────┬──────┘  └───────┬──────┘  └────────┬───────┘   └────────┬────────┘
//       │                           │                 │                  │                    │
//       │  初回のみ sessionStorage 消費   │                 │                  │                    │
//       │───────────────────────────▶                 │                  │                    │
//       │                           │                 │                  │                    │
//       │               redirect manual               │                  │                    │
//       │─────────────────────────────────────────────▶                  │                    │
//       │                           │                 │                  │                    │
//       │               opaqueredirect                │                  │                    │
//       ◀╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌│                  │                    │
//       │                           │                 │                  │                    │
//   ┌alt [復帰直後に再 302 (ループ)]───────────────────────────────────────────────────────────────────────┐
//   │   │                           │                 │                  │                    │   │
//   │   │                  cameFromEasyAuthReturn=true│                  │                    │   │
//   │   │────────────────────────────────────────────────────────────────▶                    │   │
//   │   │                           │                 │                  │                    │   │
//   │   │                           │ no-op           │                  │                    │   │
//   │   ◀╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌│                    │   │
//   │   │                           │                 │                  │                    │   │
//   │   │                           │   undefined → /auth/login          │                    │   │
//   │   │─────────────────────────────────────────────────────────────────────────────────────▶   │
//   │   │                           │                 │                  │                    │   │
//   ├[初回 302]╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌╌┤
//   │   │                           │                 │                  │                    │   │
//   │   │                      navigatingAway=true    │                  │                    │   │
//   │   │────────────────────────────────────────────────────────────────▶                    │   │
//   │   │                           │                 │                  │                    │   │
//   └─────────────────────────────────────────────────────────────────────────────────────────────┘
//       │                           │                 │                  │                    │
//   ┌───┴──┐                ┌───────┴──────┐  ┌───────┴──────┐  ┌────────┴───────┐   ┌────────┴────────┐
//   │ Page │                │ beginAttempt │  │ Cookie2Token │  │ handleRedirect │   │ checkPermission │
//   └──────┘                └──────────────┘  └──────────────┘  └────────────────┘   └─────────────────┘

// 再認証から /.auth/login/aad → 戻ってきた直後かを示す sessionStorage キー。
// 誘導直前に立て、次回 Cookie2Token 開始時に必ず消費する。
const EASY_AUTH_RETURN_KEY = "easyAuthRedirectAttempted";

// フルページ遷移を開始済みか (同一ページ寿命)。遷移後は新規ロードでリセットされる。
let navigatingAway = false;

// 再認証から戻った直後の試行か (1 回限り)。beginAttempt で確定し、その試行が 302 以外で
// 決着したら endAttempt でリセットする。並行呼び出しでも sessionStorage 読み取りは
// returnGuardConsumed により 1 回に固定し、全 waiter で一貫させる。
let returnGuardConsumed = false;
let justReturned = false;

// 真のループ (復帰直後にまた 302) を検出した永続フラグ。立ったらそのページ寿命では
// 再誘導しない (無限リダイレクト防止)。ページリロードでリセットされる。
let loopDetected = false;

/** 同一オリジンの絶対パスのみ許可する (オープンリダイレクト防止)。 */
const sanitizeReturnTo = (raw: string): string =>
  /^\/(?![/\\])/.test(raw) ? raw : "/";

export const useEasyAuthRedirect = () => {
  /**
   * Cookie2Token 呼び出し開始時に 1 回呼ぶ。sessionStorage の戻りガードを消費し、
   * 「再認証から戻った直後の試行か (justReturned)」を確定する (成功/失敗を問わず消費するため残留しない)。
   * 並行呼び出しでも最初の 1 回だけ sessionStorage を読み、以降はキャッシュ値を使う。
   */
  const beginAttempt = (): void => {
    if (returnGuardConsumed || !import.meta.client) {
      return;
    }
    returnGuardConsumed = true;
    if (window.sessionStorage.getItem(EASY_AUTH_RETURN_KEY)) {
      window.sessionStorage.removeItem(EASY_AUTH_RETURN_KEY);
      justReturned = true;
    }
  };

  /**
   * Cookie2Token が 302 以外で決着 (成功 / 非 302 失敗) したときに呼ぶ。
   * 「復帰直後」の役目は終わったので justReturned をリセットし、次の試行 (後の
   * セッション切れ) を復帰直後と誤判定しないようにする。loopDetected は保持する。
   */
  const endAttempt = (): void => {
    justReturned = false;
  };

  /**
   * Cookie2Token が EasyAuth の 302 を返したときに呼ぶ。
   * - 復帰直後 (justReturned) にまた 302 = ループ → loopDetected を立て誘導しない。
   * - 既にループ検出済み (loopDetected) / 遷移開始済み (navigatingAway) → no-op。
   * - それ以外は /.auth/login/aad へフルページ遷移して EasyAuth セッションを張り直す。
   */
  const handleEasyAuthRedirect = (): void => {
    if (!import.meta.client || navigatingAway || loopDetected) {
      return;
    }
    if (justReturned) {
      // 復帰直後にまた 302 = ループ。永続フラグを立て、再々誘導せず通常フロー (/auth/login) へ。
      loopDetected = true;
      return;
    }
    // 戻りガードを立ててからフルページ遷移。navigatingAway の設定と location.href 代入は
    // 同期・不可分なので、先行呼び出しの後に走る後続呼び出しは必ず navigatingAway=true を観測する。
    window.sessionStorage.setItem(EASY_AUTH_RETURN_KEY, "1");
    navigatingAway = true;
    const returnTo = sanitizeReturnTo(
      window.location.pathname + window.location.search + window.location.hash,
    );
    window.location.href = `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(returnTo)}`;
  };

  /**
   * EasyAuth 再認証のためフルページ遷移中か。
   * true の間は checkPermission 等が SPA 遷移 (navigateTo) を実行しないようにする。
   */
  const isNavigatingAway = (): boolean => navigatingAway;

  return { beginAttempt, endAttempt, handleEasyAuthRedirect, isNavigatingAway };
};
